import { useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { DocumentRetrievalExclusionReason, Task } from "@common/types/task";
import { pluralize } from "@common/helpers/string";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { FinancialDocumentUploadDropzone } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadDropzone";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { UploadIcon } from "@icons";
import type { RowSelectionState } from "@tanstack/react-table";
import { useTaskDocumentUpload } from "../../useTaskDocumentUpload";
import { useAddDocumentsToChat } from "../../useAddDocumentsToChat";
import { DocumentReviewDrawer } from "./DocumentReviewDrawer";
import { DocumentRetrievalBulkActions } from "./DocumentRetrievalBulkActions";
import { DocumentRetrievalDefaultActions } from "./DocumentRetrievalDefaultActions";
import { DocumentRetrievalViewTables } from "./DocumentRetrievalViewTables";
import { useDocumentRetrievalTaskModel } from "./useDocumentRetrievalTaskModel";
import { useDocumentRetrievalRowChatDrag } from "./useDocumentRetrievalRowChatDrag";
import styles from "./DocumentRetrievalTaskBody.module.scss";

interface DocumentRetrievalTaskBodyProps {
    selectedTask: Task;
}

interface DocumentRetrievalTaskBodyInnerProps extends DocumentRetrievalTaskBodyProps {
    attachDocument: (documentId: number) => void;
    attachDocuments: (documentIds: number[]) => void;
    detachDocuments: (documentIds: number[]) => Promise<boolean>;
    attachedDocumentIds: number[];
}

const DocumentRetrievalTaskBodyInner = ({
    selectedTask,
    attachDocument,
    attachDocuments,
    detachDocuments,
    attachedDocumentIds,
}: DocumentRetrievalTaskBodyInnerProps) => {
    const uploadAllButtonRef = useRef<HTMLButtonElement>(null);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [selectedDocumentIds, setSelectedDocumentIds] = useState<number[]>(
        [],
    );
    const [selectedMonthKeys, setSelectedMonthKeys] = useState<string[]>([]);
    const model = useDocumentRetrievalTaskModel({
        selectedTask,
        detachDocuments,
    });
    const { canAddToChat, addDocumentsToChat } = useAddDocumentsToChat();

    useKeyboardCommand({
        keys: "mod+enter",
        callback: () => {
            uploadAllButtonRef.current?.click();
        },
        options: {
            preventDefault: true,
            description: "Upload all",
        },
    });

    const {
        isViewOnly,
        markCompleteTooltip,
        unverifiedDocs,
        isCompleted,
        handleMarkComplete,
        handleMarkIncomplete,
        expectedCategoryIds,
        expectedAccount,
        expectedAccountNumberCandidates,
        expectedAccountNumberCandidatesLoading,
        handlePeriodStartDateOverride,
        handlePeriodEndDateOverride,
        monthKeys,
        occupiedMonths,
        previewedDoc,
        closePreview,
        openPreview,
        handleExcludeMany,
        handleRemoveFromTask,
        handleRemoveDocumentsFromTask,
        removingDocumentIds,
        handleRetry,
        isRetrying,
        retryingDocumentId,
        matchedCount,
        totalCount,
        isAllResolved,
        showCompleteConfirmation,
        setShowCompleteConfirmation,
    } = model;

    const hasBulkSelection =
        selectedDocumentIds.length > 0 || selectedMonthKeys.length > 0;
    const handleRowSelectionChange = useCallback(
        (
            selection: RowSelectionState,
            selected: { documentIds: number[]; monthKeys: string[] },
        ) => {
            setRowSelection(selection);
            setSelectedDocumentIds(selected.documentIds);
            setSelectedMonthKeys(selected.monthKeys);
        },
        [],
    );
    const handleClearBulkSelection = useCallback(() => {
        setRowSelection({});
        setSelectedDocumentIds([]);
        setSelectedMonthKeys([]);
    }, []);
    const selectedDocuments = useMemo(() => {
        const selectedIds = new Set(selectedDocumentIds);
        return model.rawDocs.filter((doc) => selectedIds.has(doc.id));
    }, [model.rawDocs, selectedDocumentIds]);
    const rowDragOut = useDocumentRetrievalRowChatDrag({
        documents: model.rawDocs,
        selectedDocuments,
        clearSelection: handleClearBulkSelection,
    });
    const handleBulkRemoveFromTask = useCallback(async () => {
        const didSucceed =
            await handleRemoveDocumentsFromTask(selectedDocumentIds);
        if (didSucceed) {
            handleClearBulkSelection();
        }
    }, [
        handleClearBulkSelection,
        handleRemoveDocumentsFromTask,
        selectedDocumentIds,
    ]);
    const handleBulkStatementUnavailable = useCallback(() => {
        handleExcludeMany(
            selectedMonthKeys,
            DocumentRetrievalExclusionReason.STATEMENT_UNAVAILABLE,
        );
        handleClearBulkSelection();
    }, [handleClearBulkSelection, handleExcludeMany, selectedMonthKeys]);
    const handleBulkAskAbout = useCallback(() => {
        if (selectedDocuments.length === 0) {
            return;
        }
        addDocumentsToChat(selectedDocuments);
        handleClearBulkSelection();
    }, [addDocumentsToChat, selectedDocuments, handleClearBulkSelection]);

    return (
        <div className={styles.container}>
            <div className={styles.subtitle}>
                <span>
                    Matched {matchedCount} of {totalCount}
                </span>
                {expectedAccount && (
                    <>
                        <span className={styles.subtitleSeparator}>·</span>
                        <span className={styles.categoryLabel}>
                            {expectedAccount.name}
                            {expectedAccount.accountNumberMask
                                ? ` ${expectedAccount.accountNumberMask}`
                                : ""}
                        </span>
                    </>
                )}
            </div>

            <FinancialDocumentUploadDropzone
                dropzoneClassName={styles.tableSection}
                cover={
                    <div className={styles.dropCover}>
                        <UploadIcon />
                        <span>Drop files to upload</span>
                    </div>
                }
            >
                <DocumentRetrievalViewTables
                    model={model}
                    rowSelection={rowSelection}
                    onRowSelectionChange={handleRowSelectionChange}
                    rowDragOut={rowDragOut}
                    footer={
                        <CommandIsland
                            sticky
                            className={classNames(
                                commandIslandStyles.bulkActionsStickyFooter,
                                styles.commandIsland,
                            )}
                        >
                            {hasBulkSelection ? (
                                <DocumentRetrievalBulkActions
                                    selectedCount={
                                        Object.values(rowSelection).filter(
                                            Boolean,
                                        ).length
                                    }
                                    selectedDocumentCount={
                                        selectedDocumentIds.length
                                    }
                                    canRemoveFromTask={
                                        selectedDocumentIds.length > 0
                                    }
                                    canMarkStatementUnavailable={
                                        selectedMonthKeys.length > 0
                                    }
                                    canAskAbout={
                                        canAddToChat &&
                                        selectedDocumentIds.length > 0
                                    }
                                    isRemoving={removingDocumentIds.length > 0}
                                    onClearSelection={handleClearBulkSelection}
                                    onRemoveFromTask={() => {
                                        void handleBulkRemoveFromTask();
                                    }}
                                    onMarkStatementUnavailable={
                                        handleBulkStatementUnavailable
                                    }
                                    onAskAbout={handleBulkAskAbout}
                                />
                            ) : (
                                <DocumentRetrievalDefaultActions
                                    uploadAllButtonRef={uploadAllButtonRef}
                                    hasUnverifiedDocs={
                                        unverifiedDocs.length > 0
                                    }
                                    isCompleted={isCompleted}
                                    isAllResolved={isAllResolved}
                                    isViewOnly={isViewOnly}
                                    markCompleteTooltip={markCompleteTooltip}
                                    attachedDocumentIds={attachedDocumentIds}
                                    attachDocument={attachDocument}
                                    attachDocuments={attachDocuments}
                                    onReviewUnmatched={() => {
                                        openPreview(unverifiedDocs[0].id);
                                    }}
                                    onMarkComplete={() =>
                                        setShowCompleteConfirmation(true)
                                    }
                                    onMarkIncomplete={handleMarkIncomplete}
                                />
                            )}
                        </CommandIsland>
                    }
                />
            </FinancialDocumentUploadDropzone>

            <ConfirmationModal
                show={showCompleteConfirmation}
                title="Mark task as complete"
                question={`All ${totalCount} ${pluralize("row", totalCount)} resolved. Mark this task as done?`}
                yes="Complete"
                no="Cancel"
                onConfirm={handleMarkComplete}
                onReject={() => setShowCompleteConfirmation(false)}
                onHide={() => setShowCompleteConfirmation(false)}
            />

            <DocumentReviewDrawer
                previewedDoc={previewedDoc}
                monthKeys={monthKeys}
                occupiedMonths={occupiedMonths}
                expectedCategoryIds={expectedCategoryIds}
                expectedAccountNumberCandidates={
                    expectedAccountNumberCandidates
                }
                expectedAccountNumberCandidatesLoading={
                    expectedAccountNumberCandidatesLoading
                }
                onClose={closePreview}
                onRetry={handleRetry}
                isRetrying={
                    isRetrying && retryingDocumentId === previewedDoc?.id
                }
                onRemoveFromTask={handleRemoveFromTask}
                isRemoving={
                    previewedDoc != null &&
                    removingDocumentIds.includes(previewedDoc.id)
                }
                onPeriodStartDateOverride={handlePeriodStartDateOverride}
                onPeriodEndDateOverride={handlePeriodEndDateOverride}
            />
        </div>
    );
};

export const DocumentRetrievalTaskBody = ({
    selectedTask,
}: DocumentRetrievalTaskBodyProps) => {
    const {
        handleUploadCompleted,
        attachDocument,
        attachDocuments,
        detachDocuments,
        attachedDocumentIds,
    } = useTaskDocumentUpload(selectedTask);

    return (
        <FinancialDocumentUploadProvider onCompleted={handleUploadCompleted}>
            <DocumentRetrievalTaskBodyInner
                selectedTask={selectedTask}
                attachDocument={attachDocument}
                attachDocuments={attachDocuments}
                detachDocuments={detachDocuments}
                attachedDocumentIds={attachedDocumentIds}
            />
        </FinancialDocumentUploadProvider>
    );
};
